import { BodyMediumMidEmphasis } from '@/design-system';
import colors from '@/theme/colors';
import { Checkbox, ListItem, ListItemButton, ListItemIcon } from '@mui/material';
import { UncheckedCheckboxIcon } from './UncheckedCheckboxIcon';
import { css } from '@emotion/css';

export type OptionType = {
  value: string;
  description: string;
  disabled?: boolean;
};

type Props = OptionType & {
  filterName: string;
  checked: boolean;
  handleToggle: (filterName: string, value: string, description: string) => () => void;
  placeholder?: string;
};

export const FilterOption: React.FC<Props> = ({
  filterName,
  value,
  description,
  disabled,
  handleToggle,
  checked,
  placeholder,
}) => {
  const inputId = `${filterName}-${value}`;
  const labelId = `${filterName}-${value}-label`;

  const accessibilityLabel = description;

  const onClick = () => {
    if (disabled) return;
    handleToggle(filterName, value, description)();
  };

  return (
    <ListItem disablePadding component="div" className={styles.listItemContainer}>
      <ListItemButton
        data-testid="itemButton"
        className={styles.listItem}
        disabled={disabled && !checked}
        tabIndex={-1}
        role="none"
        aria-checked={checked}
        aria-disabled={disabled && !checked}
      >
        <ListItemIcon onClick={onClick}>
          <Checkbox
            className={styles.checklist}
            icon={<UncheckedCheckboxIcon />}
            edge="end"
            color="secondary"
            checked={checked}
            data-testid="itemCheckbox"
            disabled={disabled && !checked}
            id={inputId}
            aria-labelledby={labelId}
            inputProps={{
              'aria-label': accessibilityLabel,
            }}
          />
        </ListItemIcon>
        <label id={labelId} htmlFor={inputId} className={styles.labelWrapper}>
          <BodyMediumMidEmphasis data-testid="itemLabel">{description}</BodyMediumMidEmphasis>
         {placeholder &&<BodyMediumMidEmphasis color={colors.slate}>&nbsp;{placeholder}</BodyMediumMidEmphasis>} 
        </label>
      </ListItemButton>
    </ListItem>
  );
};

const styles = {
  listItemContainer: css`
    &.MuiListItem-root {
      display: block;
    }
  `,
  listItem: css`
    &.MuiListItemButton-root {
      padding: 9px 0px;
      justify-content: flex-start;
    }
    & > .MuiListItemIcon-root {
      min-width: auto;
    }
  `,
  checklist: css`
    &.MuiCheckbox-root {
      color: ${colors.prussian};
      padding: 0px;
      margin-right: 8px;
    }
  `,
  labelWrapper: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    flex-grow: 1;
    white-space: nowrap;
  `,
};
